import { t } from 'i18next';
import { FC, useCallback, useState } from 'react';
import styled from 'styled-components';

import { Mvpd } from '#/client';
import { PageSpinner } from '#/components/Splash';
import { MEDIA_QUERIES } from '#/hooks';
import { useOnNavigateAway } from '#/hooks/useOnNavigateAway';
import { AppStatus } from '#/service/constants';
import { remify } from '#/service/utils';

import { focusShadow } from '../common';
import { Logo, LogoContainer } from '../Logo';
import { FullPageModal } from '../Modal/FullPageModal';
import { Status } from '../Status';
import { MvpdSelect } from './MvpdSelect';
import { useLoginTve } from './useLoginTve';

interface Props {
  closeModal: () => void;
}

/**
 * Login component for TVE authentication method (federated login)
 */
export const LoginTVE: FC<Props> = ({ closeModal }) => {
  const { error, mvpds, loading, selectProvider, awaiting, restartProcess } =
    useLoginTve(closeModal);
  const [selectedMvpdId, setSelectedMvpdId] = useState<string>('');

  useOnNavigateAway(closeModal);

  const otherMvpds =
    mvpds?.others.map((mvpd) => ({
      value: mvpd.name,
      key: mvpd.id,
    })) ?? [];

  if (error) {
    throw error;
  }

  if (loading) {
    return (
      <FullPageModal>
        <PageSpinner $startFadeout={loading} />
      </FullPageModal>
    );
  }

  if (awaiting) {
    return (
      <FullPageModal>
        <Status
          dismissMessage={t('button.cancel')}
          onDismiss={restartProcess}
          status={AppStatus.Loading}
          statusMessage={t('login.tve.processing.message')}
          statusTitle={t('login.tve.processing.title')}
        />
      </FullPageModal>
    );
  }

  return (
    <FullPageModal>
      <LogoContainer>
        <Logo isSecondary onClick={closeModal} />
      </LogoContainer>
      <Container>
        <ContainerTitle>{t('login.tve.selectYourProvider')}</ContainerTitle>

        <PromotedList data-testid="promoted-list">
          {mvpds?.promoted.map((mvpd) => (
            <PromotedButton
              aria-label={mvpd.name}
              key={mvpd.id}
              onClick={() => selectProvider(mvpd.id)}
            >
              <PromotedLogo mvpd={mvpd} />
            </PromotedButton>
          ))}
        </PromotedList>

        {otherMvpds.length > 0 && (
          <MvpdSelect
            id="mvpd-services"
            maxHeight="14rem"
            onSelect={(mvpdId) => {
              selectProvider(mvpdId);
              setSelectedMvpdId(mvpdId);
            }}
            options={otherMvpds}
            placeholder={t('login.tve.searchPlaceholder')}
            searchable
            selectedOption={otherMvpds[selectedMvpdId]}
            title={t('login.tve.searchProviders')}
          />
        )}
      </Container>
    </FullPageModal>
  );
};

const Container = styled.div`
  margin: ${remify(80)} auto 0;
  padding: 0 ${remify(50)};
  max-width: ${remify(800)};

  @media ${MEDIA_QUERIES.lg} {
    padding: ${remify(34)} ${remify(32)};
  }
  @media ${MEDIA_QUERIES.sm} {
    padding: ${remify(16)};
  }
  /* regardless of responsivity, keep a big bottom padding to prevent the combobox being on the bottom edge. */
  & {
    padding-bottom: 5rem;
  }
`;

const ContainerTitle = styled.h2`
  text-align: center;
  margin-bottom: ${remify(32)};
  font: var(--fontStyleTitle3);
  color: var(--pageHeadingForeground);
`;

const PromotedList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-auto-rows: auto;
  gap: ${remify(5)} ${remify(5)};
  margin-bottom: ${remify(24)};

  @media ${MEDIA_QUERIES.xs} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PromotedButton = styled.button`
  border: none;
  cursor: pointer;
  background-color: var(--tveFeaturedBackground);
  display: block;
  padding: ${remify(24)} ${remify(40)};
  border-radius: ${remify(5)};

  /* styles for the text fallback */
  color: var(--buttonPrimaryForeground);
  font: var(--fontStyleTitle1);

  &.focus-visible {
    ${focusShadow}
  }

  &:hover {
    opacity: 0.7;
  }

  &:active {
    background-image: linear-gradient(
      to bottom,
      var(--iapGradientTop),
      var(--iapGradientBottom)
    );
    color: var(--iapForeground);
    opacity: 1;
  }

  img {
    width: 100%;
  }

  @media ${MEDIA_QUERIES.sm} {
    padding: ${remify(10)} ${remify(24)} ${remify(6)};
  }
`;

const PromotedLogo = ({ mvpd }: { mvpd: Mvpd }) => {
  const [isError, setIsError] = useState(false);
  const onError = useCallback(() => setIsError(true), []);
  const { name, logoUrl } = mvpd;

  return isError ? (
    <span>{name}</span>
  ) : (
    <img alt={name} onError={onError} src={logoUrl} />
  );
};
